import React, { useCallback, useMemo, useState } from 'react';
import {
  Box, Paper, Table, TableHead, TableBody, TableRow, TableCell, Chip, Button, Stack,
  Dialog, DialogTitle, DialogContent, IconButton, Typography,
} from '@mui/material';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import WmsNotification, { useWmsNotification } from '../../shared/WmsNotification';
import useWmsPolling from '../../shared/useWmsPolling';
import packingService, { PackingOrderDto, CompletedPackingOrder } from '../../services/packingService';
import WmsStationHeader from '../../shared/WmsStationHeader';
import WmsScannerPanel from '../../shared/WmsScannerPanel';
import WmsListSection from '../../shared/WmsListSection';
import { WMS_TYPE_CHIP_SX } from '../../shared/wmsTypeChip';
import { useChannelLabel } from '../picking/useChannelLabel';
import PackingChannelBanner from './PackingChannelBanner';

interface PackingScanEntryProps {
  mode: 'product' | 'slot';
  cashierName: string;
  warehouseId: string;
  todayPacked: number;
  onOrderFound: (order: PackingOrderDto, source: 'product' | 'slot', slotCode?: string, slotId?: string) => void;
  /** Packed ama henuz kargoya aktarilmamis siparisi etiket + MNG ekranina acar. */
  onReopenCompleted?: (orderId: string) => void;
  onBack: () => void;
}

const PackingScanEntry: React.FC<PackingScanEntryProps> = ({ mode, cashierName, warehouseId, todayPacked, onOrderFound, onReopenCompleted, onBack }) => {
  const { t } = useTranslation('warehouse');
  const { channels } = useChannelLabel(warehouseId);
  const { notification, notify, clear } = useWmsNotification();
  // Kanal filtresi (null = hepsi). Kutu istasyonuna gore tek kanala odaklanmak icin.
  const [channelFilter, setChannelFilter] = useState<string | null>(null);

  const [completedOpen, setCompletedOpen] = useState(false);
  const [completedOrders, setCompletedOrders] = useState<CompletedPackingOrder[]>([]);
  const [completedLoading, setCompletedLoading] = useState(false);

  const { data: pendingOrders, refresh } = useWmsPolling<PackingOrderDto[]>(
    () => packingService.getPending(warehouseId),
    5000,
    !!warehouseId,
  );

  const handleOpenCompleted = useCallback(async () => {
    if (!warehouseId) return;
    setCompletedOpen(true);
    setCompletedLoading(true);
    try {
      const list = await packingService.getCompletedToday(warehouseId);
      setCompletedOrders(list);
    } catch {
      setCompletedOrders([]);
      notify({ type: 'error', message: t('screens.packing.ready.completedLoadError') });
    } finally {
      setCompletedLoading(false);
    }
  }, [warehouseId, notify, t]);

  const handleScan = useCallback(async (barcode: string) => {
    try {
      const result = await packingService.findByBarcode(warehouseId, barcode);
      if (result.success && result.data) {
        notify({ type: 'success', message: t('screens.packing.ready.orderFound', { orderNumber: result.data.order.orderNumber }) });
        onOrderFound(result.data.order, result.data.type, result.data.slotCode, result.data.slotId);
      } else {
        notify({ type: 'error', message: result.message || t('screens.packing.ready.barcodeNotRecognized') });
      }
    } catch {
      notify({ type: 'error', message: t('screens.packing.ready.scanError') });
    }
  }, [notify, onOrderFound, warehouseId, t]);

  const orders = useMemo(() => {
    const wantType = mode === 'product' ? 'Single' : 'Multi';
    return (pendingOrders || []).filter((o) => {
      if (o.orderType !== wantType) return false;
      if (channelFilter && (o.marketplaceSource || '').toLowerCase() !== channelFilter) return false;
      return true;
    });
  }, [pendingOrders, mode, channelFilter]);

  const isSlot = mode === 'slot';

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, height: '100%', display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 2.5 } }}>
      <WmsNotification notification={notification} onClose={clear} />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ color: 'text.secondary' }}>
          {t('screens.packing.common.back')}
        </Button>
      </Box>

      <WmsStationHeader
        title={isSlot ? t('screens.packing.modes.slot.title') : t('screens.packing.modes.product.title')}
        subtitle={t('screens.packing.ready.subtitle', { name: cashierName })}
        badgeLabel={t('screens.packing.ready.badge', { count: todayPacked })}
        badgeColor="success"
        badgeIcon={<Inventory2Icon />}
        onBadgeClick={handleOpenCompleted}
      />

      <WmsScannerPanel
        title={isSlot ? t('screens.packing.scanEntry.scanSlotTitle') : t('screens.packing.scanEntry.scanProductTitle')}
        placeholder={isSlot ? t('screens.packing.scanEntry.scanSlotPlaceholder') : t('screens.packing.scanEntry.scanProductPlaceholder')}
        onScan={handleScan}
        helperText={isSlot ? t('screens.packing.scanEntry.scanSlotHelper') : t('screens.packing.scanEntry.scanProductHelper')}
        maxWidth={600}
      />

      {/* Kanal filtresi (opsiyonel): kutu istasyonuna gore tek kanala odaklan. */}
      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
        <Chip
          label={t('screens.packing.scanEntry.allChannels')}
          color={channelFilter === null ? 'primary' : 'default'}
          variant={channelFilter === null ? 'filled' : 'outlined'}
          onClick={() => setChannelFilter(null)}
          size="small"
        />
        {channels.map((ch) => (
          <Chip
            key={ch.source}
            label={ch.label}
            variant={channelFilter === ch.source ? 'filled' : 'outlined'}
            onClick={() => setChannelFilter(ch.source)}
            size="small"
            sx={channelFilter === ch.source ? { bgcolor: ch.color, color: '#fff' } : { borderColor: ch.color, color: ch.color }}
          />
        ))}
      </Stack>

      {channelFilter && (
        <PackingChannelBanner source={channelFilter} />
      )}

      <WmsListSection
        title={isSlot ? t('screens.packing.scanEntry.pendingSlotTitle') : t('screens.packing.scanEntry.pendingProductTitle')}
        count={orders.length}
        onRefresh={refresh}
        hasData={orders.length > 0}
        emptyText={t('screens.packing.ready.pendingEmpty')}
      >
        <Paper sx={{ bgcolor: 'background.paper', overflow: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('screens.packing.ready.colOrderNo')}</TableCell>
                <TableCell>{t('screens.packing.ready.colCustomer')}</TableCell>
                <TableCell align="center">{t('screens.packing.ready.colProduct')}</TableCell>
                <TableCell align="center">{t('screens.packing.scanEntry.colChannel')}</TableCell>
                <TableCell align="center">{t('screens.packing.ready.colStatus')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orders.map((order) => (
                <TableRow
                  key={order.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => onOrderFound(order, isSlot ? 'slot' : 'product')}
                >
                  <TableCell sx={{ fontWeight: 600 }}>{order.orderNumber}</TableCell>
                  <TableCell>{order.customerName}</TableCell>
                  <TableCell align="center">{t('screens.packing.common.itemsCount', { count: order.itemCount })}</TableCell>
                  <TableCell align="center">
                    <PackingChannelBanner source={order.marketplaceSource} variant="chip" />
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#4caf50', display: 'inline-block' }} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      </WmsListSection>

      <Dialog open={completedOpen} onClose={() => setCompletedOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Inventory2Icon color="success" />
            {t('screens.packing.ready.completedTitle')}
          </Box>
          <IconButton onClick={() => setCompletedOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {completedLoading ? (
            <Typography sx={{ py: 2, textAlign: 'center' }} color="text.secondary">{t('screens.packing.ready.loading')}</Typography>
          ) : completedOrders.length === 0 ? (
            <Typography sx={{ py: 2, textAlign: 'center' }} color="text.secondary">{t('screens.packing.ready.completedEmpty')}</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('screens.packing.ready.colTime')}</TableCell>
                  <TableCell>{t('screens.packing.ready.colOrderNo')}</TableCell>
                  <TableCell>{t('screens.packing.ready.colCustomer')}</TableCell>
                  <TableCell align="center">{t('screens.packing.ready.colProduct')}</TableCell>
                  <TableCell align="center">{t('screens.packing.ready.colCargo')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {completedOrders.map((order) => {
                  const awaitingCargo = !order.trackingNumber;
                  return (
                  <TableRow
                    key={order.id}
                    hover={awaitingCargo && !!onReopenCompleted}
                    sx={{
                      cursor: awaitingCargo && onReopenCompleted ? 'pointer' : 'default',
                      opacity: awaitingCargo ? 1 : 0.7,
                    }}
                    onClick={() => {
                      if (!awaitingCargo || !onReopenCompleted) return;
                      setCompletedOpen(false);
                      onReopenCompleted(order.id);
                    }}
                  >
                    <TableCell>
                      {order.packedAt
                        ? new Date(order.packedAt).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })
                        : '-'}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{order.orderNumber}</TableCell>
                    <TableCell>{order.customerName}</TableCell>
                    <TableCell align="center">{t('screens.packing.common.itemsCount', { count: order.itemCount })}</TableCell>
                    <TableCell align="center">
                      {awaitingCargo ? (
                        <Chip
                          label={t('screens.packing.ready.awaitingCargo')}
                          size="small"
                          color="warning"
                          sx={WMS_TYPE_CHIP_SX}
                        />
                      ) : (
                        <Chip
                          label={order.shippingMethod
                            ? t('screens.packing.ready.cargoShipped', {
                                carrier: order.shippingMethod,
                                tracking: order.trackingNumber || t('screens.packing.ready.cargoDone'),
                              })
                            : (order.trackingNumber || t('screens.packing.ready.cargoDone'))}
                          size="small"
                          color="success"
                          variant="outlined"
                          sx={WMS_TYPE_CHIP_SX}
                          title={order.trackingNumber || undefined}
                        />
                      )}
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default PackingScanEntry;
